import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatIST, itemsLabel, itemNames } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { openAuthedFile } from '@/lib/fileDownload';
import { getProductTypeName, canSeeFinancials, canEditOrders, canCompleteOrders } from '@/lib/constants';
import { Plus, Search, ChevronLeft, ChevronRight, Eye, Pencil, AlertCircle, RefreshCw, Trash2, AlertTriangle, CheckCircle2, ListChecks, Printer, X } from 'lucide-react';
import { toast } from 'sonner';

import { Screen, CommandBar, StatStrip, DataGrid, Pane, CellButton } from '@/components/data';
import PrintListDialog from '@/pages/Orders/PrintListDialog';
import PhoneOrdersList from '@/pages/Orders/PhoneOrdersList';
import { useIsMobile } from '@/hooks/useIsMobile';
import { STATUS_MAP, PRIORITY_MAP } from '@/lib/status-colors';

/**
 * Every order, in one framed screen.
 *
 * This used to be a document: a title, a filter card, then fifteen 64px rows
 * that pushed the pagination off the bottom. Five of its ten columns were hidden
 * below `xl` purely because the rows were tall — not because anybody decided
 * those columns did not matter.
 *
 * At 30px a row all ten columns fit and roughly forty rows are visible, so the
 * page size goes to fifty and the list scrolls under its own sticky header
 * rather than the page scrolling under the app.
 */

const PAGE_SIZE = 50;

// The lifecycle phases in the order work moves through them. Counts come from
// the server across the whole filtered set, not just the page on screen, so
// clicking one to filter does not make the others move.
const PHASES = [
  ['design', 'Design'],
  ['in_production', 'Production'],
  ['ready_for_dispatch', 'Ready'],
  ['dispatched', 'Dispatched'],
  ['delivered', 'Delivered'],
];

// One coarse question asked above the phase chips: is this order still open, or
// is it done with? Delivered work leaves the working list without leaving the
// app, which is why the list opens on Active.
const SCOPES = [
  ['all', 'All'],
  ['active', 'Active'],
  ['delivered', 'Delivered'],
];

// an order that has already ended has nothing left to close out
const FINISHED_STATUSES = ['delivered', 'stocked'];

const PRIORITY_TEXT = {
  high: 'text-red-600',
  medium: 'text-amber-600',
  low: 'text-emerald-600',
};

export default function OrdersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [scope, setScope] = useState('active');
  const [priorityFilter, setPriorityFilter] = useState(null);

  // { action: 'delete' | 'complete', orders: [...] } — the one pending
  // confirmation, whether it came from a row button or from the selection
  const [confirming, setConfirming] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const isAdmin = user?.role === 'admin';
  const showFinancials = canSeeFinancials(user);
  const showEdit = canEditOrders(user);
  const showComplete = canCompleteOrders(user);
  // Acting on several orders at once is a deliberate errand, not the reason the
  // list is open. The column of checkboxes costs every reader a column and
  // every row a target they did not ask for, so it appears only once somebody
  // presses Select — and both bulk actions share the one mode.
  const canSelect = isAdmin || showComplete;

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    // a tick belongs to the row it was put on, and the rows are about to change
    setSelected(new Set());
    setSelecting(false);
    try {
      const params = { page, limit: PAGE_SIZE, scope };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const res = await api.get('/orders', { params });
      setOrders(res.data.orders || []);
      setTotal(res.data.total || 0);
      setStatusCounts(res.data.status_counts || {});
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to load orders';
      setError(message);
      toast.error('Failed to load orders', { description: message });
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, scope, priorityFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const toggleRow = useCallback((id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);

  const pageSelected = orders.length > 0 && orders.every((order) => selected.has(order.id));

  const toggleAll = useCallback(() => setSelected((prev) => {
    const everyOne = orders.length > 0 && orders.every((order) => prev.has(order.id));
    return everyOne ? new Set() : new Set(orders.map((order) => order.id));
  }), [orders]);

  const selectedOrders = useMemo(
    () => orders.filter((order) => selected.has(order.id)),
    [orders, selected]
  );

  // One request for one order and for fifty: the bulk endpoints report per
  // order, so a selection with an order somebody else already dealt with still
  // goes through and says which one it left alone.
  const runAction = async () => {
    if (!confirming) return;
    const { action, orders: targets } = confirming;
    const path = action === 'delete' ? '/orders/bulk-delete' : '/orders/bulk-complete';
    setActionLoading(true);
    try {
      const { data } = await api.post(path, { order_ids: targets.map((order) => order.id) });
      const done = action === 'delete' ? data.deleted_count : data.completed_count;
      if (done) {
        const what = done === 1 ? `Order ${targets[0].order_id}` : `${done} orders`;
        toast.success(action === 'delete'
          ? `${what} deleted. Restore from the Recycle Bin.`
          : `${what} marked as delivered.`);
      }
      if (data.skipped_count) {
        toast.warning(`${data.skipped_count} order(s) skipped`, {
          description: data.skipped.map((s) => `${s.order_id}: ${s.reason}`).join(', '),
        });
      }
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail
        || (action === 'delete' ? 'Failed to delete orders' : 'Failed to complete orders'));
    }
    setActionLoading(false);
    setConfirming(null);
  };

  // What the list is currently showing, in the shape both the count query and
  // the sheet take — so the dialog counts exactly what it is about to print.
  const printFilters = useMemo(() => {
    const params = { scope };
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    if (priorityFilter) params.priority = priorityFilter;
    return params;
  }, [scope, search, statusFilter, priorityFilter]);

  const printFilterLabel = useMemo(() => [
    statusFilter ? (PHASES.find(([key]) => key === statusFilter)?.[1] || statusFilter)
      : SCOPES.find(([key]) => key === scope)?.[1],
    priorityFilter && `${priorityFilter} priority`,
    search && `matching "${search}"`,
  ].filter(Boolean).join(' · '), [scope, search, statusFilter, priorityFilter]);

  // The sheet accounts writes collections on: the window the dialog settled on,
  // every page of it, with the payment status column left blank.
  const printPaymentList = async (selection) => {
    setPrinting(true);
    try {
      await openAuthedFile(`/orders/payment-list/pdf?${new URLSearchParams(selection)}`);
      setPrintOpen(false);
    } catch (err) {
      toast.error(err?.response?.status === 403
        ? 'You cannot print the order payment list'
        : 'Could not open the order payment list');
    }
    setPrinting(false);
  };

  // Scope and the phase chips answer the same question at different
  // granularities, so picking one clears the other — the strip always says
  // exactly which orders are on screen.
  const selectStatus = (key) => {
    setStatusFilter(key);
    if (key) setScope('all');
    setPage(1);
  };

  const selectScope = (key) => {
    setScope(key);
    setStatusFilter(null);
    setPage(1);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const stats = useMemo(() => ([
    { key: null, label: 'orders', value: total.toLocaleString(), tone: 'blue' },
    ...PHASES.map(([key, label]) => ({
      key,
      label,
      value: statusCounts[key] || 0,
      tone: key === 'ready_for_dispatch' ? 'good' : 'default',
      title: `Show only orders at ${label}`,
    })),
  ]), [total, statusCounts]);

  const columns = useMemo(() => ([
    selecting && {
      key: 'select',
      width: '2.25rem',
      header: (
        <Checkbox
          checked={pageSelected}
          onCheckedChange={toggleAll}
          aria-label="Select every order on this page"
          className="h-3.5 w-3.5"
          data-testid="select-all-orders"
        />
      ),
      render: (order) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selected.has(order.id)}
            onCheckedChange={() => toggleRow(order.id)}
            aria-label={`Select order ${order.order_id}`}
            className="h-3.5 w-3.5"
            data-testid={`select-order-${order.order_id}`}
          />
        </span>
      ),
    },
    {
      key: 'item',
      header: 'Item / Customer',
      width: 'minmax(13rem,1.5fr)',
      render: (order) => (
        <>
          {/* an order can be several products; the row says how many rather
              than naming the first and hiding the rest */}
          <span className="block truncate font-semibold text-slate-900"
            title={itemNames(order.line_items)}>
            {itemsLabel(order.line_items, order.customer_name)}
          </span>
          {/* the firm is who the order is for; the person is who to call about it */}
          <span className="block truncate text-[11px] leading-tight text-slate-500">
            {order.firm_name || order.customer_name}
            {order.firm_name && order.customer_name ? ` — ${order.customer_name}` : ''}
          </span>
        </>
      ),
    },
    {
      key: 'order_id',
      header: 'Order',
      width: '10rem',
      render: (order) => (
        <span className="block truncate text-[11px] text-slate-400" title={order.voice_number}>
          {order.order_id}
        </span>
      ),
    },
    {
      key: 'dept_code',
      mobile: false,
      header: 'Type',
      width: '7rem',
      render: (order) => (
        <span className="block truncate text-[11px] text-slate-600">
          {getProductTypeName(order.dept_code)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '9.5rem',
      render: (order) => (
        <span className="whitespace-nowrap" data-testid={`order-status-${order.order_id}`}>
          <Badge className={`text-[10px] ${STATUS_MAP[order.status]?.class || ''}`}>
            {order.location?.label || STATUS_MAP[order.status]?.label || order.status}
          </Badge>
          {order.location?.others > 0 && (
            <span
              className="ml-1 text-[10px] text-slate-400"
              title="Items of this order are at more than one process"
            >
              +{order.location.others}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '5.5rem',
      render: (order) => (
        <span className={`text-[11px] font-medium ${PRIORITY_TEXT[order.priority] || 'text-slate-500'}`}>
          {PRIORITY_MAP[order.priority]?.label || order.priority}
        </span>
      ),
    },
    {
      key: 'created_at',
      mobile: false,
      header: 'Placed',
      width: '9.5rem',
      render: (order) => (
        <span
          className="whitespace-nowrap text-[11px] text-slate-600"
          data-testid={`order-placed-${order.order_id}`}
        >
          {order.created_at ? (
            <>
              {formatIST(order.created_at, { dateOnly: true })}
              <span className="ml-1 text-slate-400">{formatIST(order.created_at, { timeOnly: true })}</span>
            </>
          ) : '-'}
        </span>
      ),
    },
    {
      key: 'created_by_name',
      mobile: false,
      header: 'By',
      width: '7.5rem',
      render: (order) => (
        <span className="block truncate text-[11px] text-slate-600">{order.created_by_name || '—'}</span>
      ),
    },
    {
      key: 'expected_delivery_date',
      header: 'Delivery',
      width: '6.5rem',
      render: (order) => (
        <span className="whitespace-nowrap text-[11px] text-slate-600">
          {order.expected_delivery_date ? formatIST(order.expected_delivery_date, { dateOnly: true }) : '—'}
        </span>
      ),
    },
    showFinancials && {
      key: 'order_value',
      mobile: false,
      header: 'Value',
      align: 'right',
      width: '7rem',
      render: (order) => (
        <span className="font-medium text-slate-900">
          {order.order_value != null ? `₹${order.order_value.toLocaleString()}` : '—'}
        </span>
      ),
      total: (rows) => {
        const sum = rows.reduce((acc, o) => acc + (o.order_value || 0), 0);
        return sum ? `₹${sum.toLocaleString()}` : '';
      },
    },
    {
      key: 'actions',
      header: '',
      width: '8.5rem',
      align: 'right',
      render: (order) => (
        <span className="flex items-center justify-end gap-0.5">
          <CellButton
            icon={Eye}
            label="Open order"
            iconOnly
            onClick={() => navigate(`/orders/${order.id}`)}
            testId={`view-order-${order.order_id}`}
          />
          {showEdit && (
            <CellButton
              icon={Pencil}
              label="Edit order"
              iconOnly
              onClick={() => navigate(`/orders/${order.id}/edit`)}
              testId={`edit-order-${order.order_id}`}
            />
          )}
          {showComplete && !FINISHED_STATUSES.includes(order.status) && (
            <CellButton
              icon={CheckCircle2}
              label="Mark as delivered"
              iconOnly
              tone="good"
              onClick={() => setConfirming({ action: 'complete', orders: [order] })}
              testId={`complete-order-${order.order_id}`}
            />
          )}
          {isAdmin && (
            <CellButton
              icon={Trash2}
              label="Delete order"
              iconOnly
              tone="danger"
              onClick={() => setConfirming({ action: 'delete', orders: [order] })}
              testId={`delete-order-${order.order_id}`}
            />
          )}
        </span>
      ),
    },
  ].filter(Boolean)), [isAdmin, showEdit, showFinancials, showComplete, selecting,
    selected, pageSelected, toggleRow, toggleAll, navigate]);

  const deleting = confirming?.action === 'delete';
  const targets = confirming?.orders || [];
  const targetLabel = targets.length === 1
    ? `Order #${targets[0].order_id}`
    : `${targets.length} orders`;
  const targetItemCount = targets.reduce((sum, order) => sum + (order.line_items?.length || 0), 0);

  const confirmDialog = (
    <AlertDialog open={!!confirming} onOpenChange={(open) => { if (!open) setConfirming(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {deleting
              ? <AlertTriangle className="h-5 w-5 text-amber-500" />
              : <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
            {deleting ? 'Delete' : 'Mark'} {targetLabel}{deleting ? '?' : ' as delivered?'}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            {deleting ? (
              <>
                <span className="block">
                  This removes {targetItemCount} line item(s) and their job cards.
                </span>
                <span className="block text-slate-500">This can be undone from the Recycle Bin.</span>
              </>
            ) : (
              <>
                <span className="block">
                  Every stage still open is closed and the goods recorded as delivered.
                  Nothing is sent to the floor — use this for work that was finished long ago.
                </span>
                <span className="block text-slate-500">
                  Undoing it means moving the batches back by hand, one stage at a time.
                </span>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); runAction(); }}
            disabled={actionLoading}
            className={deleting
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-emerald-600 text-white hover:bg-emerald-700'}
            data-testid="confirm-bulk-action-btn"
          >
            {actionLoading
              ? (deleting ? 'Deleting...' : 'Completing...')
              : (deleting ? 'Delete' : 'Mark Delivered')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // one card per order below lg; the same state, the same confirmations
  if (isMobile) {
    return (
      <>
        <PhoneOrdersList
          orders={orders} total={total} loading={loading} error={error}
          page={page} totalPages={totalPages} onPage={setPage}
          onSearch={(text) => { setSearch(text); setPage(1); }}
          phases={PHASES} scopes={SCOPES}
          scope={scope} statusFilter={statusFilter} statusCounts={statusCounts}
          onScope={selectScope} onStatus={selectStatus}
          priorityFilter={priorityFilter}
          onPriority={(level) => { setPriorityFilter(level); setPage(1); }}
          showFinancials={showFinancials} showEdit={showEdit} showComplete={showComplete} isAdmin={isAdmin}
          onRefresh={fetchOrders}
          onNew={() => navigate('/orders/new')}
          onOpen={(order) => navigate(`/orders/${order.id}`)}
          onEdit={(order) => navigate(`/orders/${order.id}/edit`)}
          onComplete={(order) => setConfirming({ action: 'complete', orders: [order] })}
          onDelete={(order) => setConfirming({ action: 'delete', orders: [order] })}
        />
        {confirmDialog}
      </>
    );
  }

  return (
    <Screen data-testid="orders-page">
      <CommandBar
        title="Orders"
        subtitle={total ? `${total.toLocaleString()} total` : undefined}
        actions={[
          canSelect && !selecting && {
            key: 'select',
            label: 'Select',
            icon: ListChecks,
            title: 'Pick several orders to delete or mark delivered',
            onClick: () => setSelecting(true),
            testId: 'select-mode-btn',
          },
          selecting && showComplete && {
            key: 'bulk-complete',
            label: `Mark Delivered (${selectedOrders.length})`,
            icon: CheckCircle2,
            tone: 'good',
            disabled: selectedOrders.length === 0,
            onClick: () => setConfirming({ action: 'complete', orders: selectedOrders }),
            testId: 'bulk-complete-btn',
          },
          selecting && isAdmin && {
            key: 'bulk-delete',
            label: `Delete (${selectedOrders.length})`,
            icon: Trash2,
            tone: 'danger',
            disabled: selectedOrders.length === 0,
            onClick: () => setConfirming({ action: 'delete', orders: selectedOrders }),
            testId: 'bulk-delete-btn',
          },
          selecting && {
            key: 'cancel-selection',
            label: 'Cancel',
            icon: X,
            onClick: () => { setSelecting(false); setSelected(new Set()); },
            testId: 'cancel-selection-btn',
          },
          showFinancials && {
            key: 'print-list',
            label: 'Print Orders',
            icon: Printer,
            title: 'Print this list as a payment sheet — pick the dates and how many orders to include',
            onClick: () => setPrintOpen(true),
            testId: 'print-order-list-btn',
          },
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchOrders },
          {
            key: 'new',
            label: 'New Order',
            icon: Plus,
            tone: 'accent',
            mnemonic: 'n',
            onClick: () => navigate('/orders/new'),
            testId: 'create-order-btn',
          },
        ]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="orders-search-input"
            className="h-7 pl-8 text-xs"
            placeholder="Order ID, voice number, customer…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={stats}
        activeKey={statusFilter}
        onSelect={selectStatus}
        data-testid="orders-filters"
      >
        <span className="text-[10px] uppercase tracking-wider text-slate-400">Show</span>
        {SCOPES.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => selectScope(key)}
            aria-pressed={scope === key}
            data-testid={`scope-filter-${key}`}
            className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
              scope === key
                ? 'bg-brand-blue/10 text-brand-blue ring-1 ring-inset ring-brand-blue/30'
                : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            {label}
          </button>
        ))}
        <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">Priority</span>
        {['high', 'medium', 'low'].map((level) => (
          <button
            key={level}
            type="button"
            onClick={() => { setPriorityFilter(priorityFilter === level ? null : level); setPage(1); }}
            aria-pressed={priorityFilter === level}
            data-testid={`priority-filter-${level}`}
            className={`rounded px-2 py-1 text-[11px] font-medium capitalize transition-colors ${
              priorityFilter === level
                ? 'bg-brand-blue/10 text-brand-blue ring-1 ring-inset ring-brand-blue/30'
                : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            {level}
          </button>
        ))}
      </StatStrip>

      {error ? (
        <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-red-100 bg-white">
          <AlertCircle className="mb-3 h-10 w-10 text-red-400" />
          <p className="mb-1 font-medium text-red-600">Failed to load orders</p>
          <p className="mb-4 font-body text-sm text-slate-500">{error}</p>
          <Button onClick={fetchOrders} className="bg-brand-blue text-white hover:bg-brand-blue-dark">
            <RefreshCw className="mr-2 h-4 w-4" /> Try Again
          </Button>
        </div>
      ) : (
        <Pane
          title="Orders"
          aside={total ? `${Math.min((page - 1) * PAGE_SIZE + 1, total)}–${Math.min(page * PAGE_SIZE, total)} of ${total}` : undefined}
          scroll={false}
          bodyClassName="flex flex-col"
          actions={totalPages > 1 && (
            <span className="flex items-center gap-1" data-testid="orders-pagination">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                data-testid="prev-page-btn"
                aria-label="Previous page"
                className="rounded p-0.5 text-white/80 transition-colors hover:bg-white/20 disabled:opacity-30"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <span className="text-[10px] font-normal normal-case tracking-normal text-blue-100">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                data-testid="next-page-btn"
                aria-label="Next page"
                className="rounded p-0.5 text-white/80 transition-colors hover:bg-white/20 disabled:opacity-30"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </span>
          )}
        >
          <DataGrid
            data-testid="orders-table"
            columns={columns}
            rows={orders}
            loading={loading}
            onRowClick={(order) => navigate(`/orders/${order.id}`)}
            getRowId={(order) => order.order_id}
            showTotals={orders.length > 0 && orders.some(o => o.order_value != null)}
            empty={
              <span className="flex flex-col items-center gap-3">
                No orders found
                <Button
                  onClick={() => navigate('/orders/new')}
                  className="bg-brand-blue text-white hover:bg-brand-blue-dark"
                  data-testid="empty-create-btn"
                >
                  Create First Order
                </Button>
              </span>
            }
          />
        </Pane>
      )}

      <PrintListDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        filters={printFilters}
        filterLabel={printFilterLabel}
        onPrint={printPaymentList}
        printing={printing}
      />

      {confirmDialog}

    </Screen>
  );
}
